import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ChartBarInvestment from "./ChartBarInvestment";
import { getInvestmentManagementData } from "../../viewServices/apiCalls";
import {
  CardLoadingState,
  CardErrorState,
  CardEmptyState,
  CardNoDataState,
} from "../shared";

const MIN_HEIGHT = 180;

/**
 * Extrair dados de DD Discounts do P&L
 */
const extractDDDiscountData = (
  plStructure,
  actualValues,
  forecastValues,
  policyValues,
) => {
  // Filtrar apenas itens de nível 3 que são filhos de "total_dd_discounts"
  const ddDiscounts = plStructure.filter(
    (row) => row.level === 3 && row.parent === "total_dd_discounts",
  );

  // Se não houver dados, retornar estrutura vazia
  if (ddDiscounts.length === 0) {
    return {
      labels: [],
      datasets: [],
    };
  }

  // Preparar datasets (um por categoria de DD)
  const datasets = ddDiscounts.map((dd) => ({
    label: dd.name,
    data: [
      actualValues[dd.meta_name] || 0,
      forecastValues[dd.meta_name] || 0,
      policyValues[dd.meta_name] || 0,
    ],
  }));

  return {
    labels: ["Actual", "Forecast", "Policy"],
    datasets: datasets,
  };
};

export default function CardInvestmentManagement({ programId, filters }) {
  const [chartData, setChartData] = useState(null);
  const [currencyUnit, setCurrencyUnit] = useState("USD");
  const [isLoading, setIsLoading] = useState(!!programId);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPnlData = async () => {
      if (!programId) {
        setIsLoading(false);
        setChartData(null);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch data by program - returns data_by_type (actual/forecast/policy)
        const response = await getInvestmentManagementData(programId, filters);
        const dataByType = response.data_by_type || {};

        // Extract P&L data for each scenario type
        const actualData = dataByType.actual;
        const forecastData = dataByType.forecast;
        const policyData = dataByType.policy;

        // Use first available data for structure
        const baseData = actualData || forecastData || policyData;

        if (
          !baseData?.plStructure?.current ||
          !Array.isArray(baseData.plStructure.current)
        ) {
          setChartData(null);
          return;
        }

        const plStructure = baseData.plStructure.current;
        const actualValues = actualData?.plValues?.[1] || {};
        const forecastValues = forecastData?.plValues?.[1] || {};
        const policyValues = policyData?.plValues?.[1] || {};

        // Extrair dados de DD Discounts
        const extractedData = extractDDDiscountData(
          plStructure,
          actualValues,
          forecastValues,
          policyValues,
        );

        setChartData(extractedData);
        setCurrencyUnit(baseData.currencyUnit || "USD");
      } catch (err) {
        console.error(
          "Error fetching P&L data for Investment Management:",
          err,
        );
        setError(err.message || "Failed to load investment data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPnlData();
  }, [programId, filters]);

  // Loading state
  if (isLoading) {
    return <CardLoadingState minHeight={MIN_HEIGHT} />;
  }

  // Error state
  if (error) {
    return <CardErrorState message={error} minHeight={MIN_HEIGHT} />;
  }

  // Empty state (no program selected)
  if (!programId) {
    return <CardEmptyState minHeight={MIN_HEIGHT} />;
  }

  // No data state
  if (!chartData || chartData.datasets.length === 0) {
    return (
      <CardNoDataState
        title="Nenhum dado de investimento disponível"
        subtitle="Verifique os DD Discounts no P&L"
        minHeight={MIN_HEIGHT}
      />
    );
  }

  return <ChartBarInvestment data={chartData} currencyUnit={currencyUnit} />;
}

CardInvestmentManagement.propTypes = {
  programId: PropTypes.number,
  filters: PropTypes.object,
};
